import React, { PureComponent } from 'react';

import { FormattedMessage, injectIntl, InjectedIntl } from 'react-intl';

import { Checkbox } from '@atlaskit/checkbox';
import Tooltip from '@atlaskit/tooltip';

import PageableTable, {
  ColumnDefinitionDesktopOnly,
} from 'src/components/pageable-table';
import { BranchingModelState } from 'src/redux/branches/reducers/branch-list-reducer';
import { shouldAllowBulkDelete } from 'src/redux/branches/utils';

import { DetailedBranch, Sort } from '../../types';

import BranchRow from './branch-row';
import SortableTableHeader from './branch-sort-link';
import messages from './branch-table.i18n';
import * as styles from './branch-table.styled';

type Props = {
  intl: InjectedIntl;
  sort: Sort;
  onSortChange: (sort: Sort) => void;
  isLoading: boolean;
  isError: boolean;
  branches: DetailedBranch[];
  mainBranch: DetailedBranch;
  staleFilterSelected: boolean;
  focusedRowIndex: number | null | undefined;
  onLoadPage: () => void;
  renderPagination?: (() => JSX.Element) | null;
  selectedBranches: DetailedBranch[];
  isInSelectionMode: boolean;
  onToggleBranchSelect: (branch: DetailedBranch) => void;
  onToggleBranchSelectAllInPage: (isSelected: boolean) => void;
  branchingModel: BranchingModelState;
};

class BranchTable extends PureComponent<Props> {
  isSelected = (branch: DetailedBranch) =>
    Boolean(this.props.selectedBranches.find(br => br.name === branch.name));

  isHeaderSelected = () => {
    const { branches, branchingModel } = this.props;
    for (const branch of branches) {
      // If at least one branch is not selected return false
      if (
        shouldAllowBulkDelete(branch, branchingModel) &&
        !this.isSelected(branch)
      ) {
        return false;
      }
    }
    return true;
  };

  renderHeaders = () => {
    const { intl, sort, onSortChange, isLoading, isError } = this.props;
    const sortProps = { sort, onSortChange, isDisabled: isLoading || isError };

    return (
      <tr>
        {this.props.isInSelectionMode && (
          <styles.BranchSelectionHeader
            width="3%"
            aria-label={intl.formatMessage(messages.select)}
          >
            <Checkbox
              onChange={e =>
                this.props.onToggleBranchSelectAllInPage(e.target.checked)
              }
              isChecked={this.isHeaderSelected()}
            />
          </styles.BranchSelectionHeader>
        )}
        <SortableTableHeader
          data-qa="branches-list-header"
          ariaLabelText={intl.formatMessage(messages.name)}
          field="name"
          label={intl.formatMessage(messages.headerName)}
          scope="col"
          {...sortProps}
        />
        <SortableTableHeader
          width="12.5%"
          ariaLabelText={intl.formatMessage(messages.behind)}
          field="behind"
          label={intl.formatMessage(messages.headerBehind)}
          desktopOnly
          textAlign="right"
          scope="col"
          {...sortProps}
        />
        <SortableTableHeader
          width="12.5%"
          ariaLabelText={intl.formatMessage(messages.ahead)}
          field="ahead"
          label={intl.formatMessage(messages.headerAhead)}
          desktopOnly
          scope="col"
          {...sortProps}
        />
        <SortableTableHeader
          width="17%"
          ariaLabelText={intl.formatMessage(messages.updated)}
          field="target.date"
          label={intl.formatMessage(messages.headerUpdated)}
          desktopOnly
          scope="col"
          {...sortProps}
        />
        <styles.PullRequestTableHeader
          textAlign="center"
          width="10%"
          tabIndex={0}
          aria-label={intl.formatMessage(messages.pullRequest)}
          scope="col"
          role="columnheader"
        >
          <Tooltip content={intl.formatMessage(messages.pullRequest)}>
            <FormattedMessage {...messages.headerPullRequest} />
          </Tooltip>
        </styles.PullRequestTableHeader>

        <styles.BuildsTableHeader
          textAlign="center"
          width="7%"
          tabIndex={0}
          aria-label={intl.formatMessage(messages.builds)}
          scope="col"
          role="columnheader"
        >
          <Tooltip content={intl.formatMessage(messages.builds)}>
            <styles.BuildsHeaderWrapper>
              <FormattedMessage {...messages.headerBuilds} />
            </styles.BuildsHeaderWrapper>
          </Tooltip>
        </styles.BuildsTableHeader>

        <styles.ActionsTableHeader
          textAlign="center"
          width="5%"
          tabIndex={0}
          aria-label={intl.formatMessage(messages.headerActions)}
          scope="col"
          role="columnheader"
        >
          <FormattedMessage {...messages.headerActions} />
        </styles.ActionsTableHeader>
      </tr>
    );
  };

  renderColumnDefinitions = () => {
    return (
      <colgroup>
        <col />
        <ColumnDefinitionDesktopOnly />
        <ColumnDefinitionDesktopOnly />
        <ColumnDefinitionDesktopOnly />
        <ColumnDefinitionDesktopOnly />
        <col />
        <col />
      </colgroup>
    );
  };

  render() {
    const {
      branches,
      mainBranch,
      isLoading,
      isError,
      staleFilterSelected,
      focusedRowIndex,
      onLoadPage,
      renderPagination,
      onToggleBranchSelect,
      isInSelectionMode,
      branchingModel,
    } = this.props;

    return (
      <PageableTable
        isError={isError}
        isLoading={isLoading}
        onClickErrorState={onLoadPage}
        renderColumnDefinitions={this.renderColumnDefinitions}
        renderHeaders={this.renderHeaders}
        renderPagination={renderPagination}
        rows={branches}
      >
        {(branch, index) => {
          const developmentBranchHash =
            this.props.branchingModel.development?.target?.hash;
          const prodBranchHash =
            this.props.branchingModel.production?.target?.hash;
          const shouldRenderBranch = !(
            staleFilterSelected &&
            (branch.isMainBranch ||
              branch.target.hash === (developmentBranchHash || prodBranchHash))
          );

          return shouldRenderBranch && !isLoading ? (
            <BranchRow
              key={branch.name}
              branch={branch}
              mainBranch={mainBranch}
              isFocused={index === focusedRowIndex}
              isLoading={isLoading}
              isSelected={this.isSelected(branch)}
              staleFilterSelected={staleFilterSelected}
              isInSelectionMode={isInSelectionMode}
              onToggleBranchSelect={onToggleBranchSelect}
              branchingModel={branchingModel}
            />
          ) : (
            <></>
          );
        }}
      </PageableTable>
    );
  }
}

export default injectIntl(BranchTable);
